import React from "react";
import { Button as MUIButton, ButtonProps } from "@mui/material";
import { useTheme } from "contexts";
import { EnumTheme } from "utils/enums";

type Props = ButtonProps & {
  sx?: any;
  text?: boolean;
  submit?: boolean;
  tabIndex?: number;
  secondary?: boolean;
  tactile?: boolean;
  target?: string;
  rel?: string;
  component?: any;
  children: string | React.ReactNode;
  onClick?: (event: any) => void;
};

export const Button = ({ tactile = true, ...props }: Props) => {
  const { theme } = useTheme();
  const accentColor = props.sx?.backgroundColor ?? theme.color.primary.color;
  const contrastColor = props.sx?.color ?? theme.color.primary.text;
  const isDark = theme.id === EnumTheme.Dark;

  function handleClick(event: any) {
    props.onClick && props.onClick(event);
  }

  const borderStyle = props.secondary
    ? `1px solid ${theme.color.border.color}`
    : `1px solid ${isDark ? "#b1c5ff" : "#191b22"}`;

  const boxShadow = props.secondary
    ? `2px 2px 0px 0px ${theme.color.border.color}`
    : `3px 3px 0px 0px ${isDark ? "#000000" : "#191b22"}`;

  const hoverBoxShadow = props.secondary
    ? `3px 3px 0px 0px ${isDark ? "#f0f0f9" : "#191b22"}`
    : `4px 4px 0px 0px ${isDark ? "#000000" : "#191b22"}`;

  const componentProp = props.href ? "a" : undefined;

  return (
    <MUIButton
      component={componentProp as any}
      size={props.size}
      href={props.href}
      target={props.target}
      rel={props.rel}
      disabled={props.disabled}
      tabIndex={props.tabIndex}
      variant={props.secondary ? "outlined" : props.text ? "text" : "contained"}
      sx={{
        fontWeight: theme.font.semiBold,
        textTransform: "none",
        borderRadius: 0,
        border: props.text ? "none" : borderStyle,
        boxShadow: props.text ? "none" : boxShadow,
        backgroundColor:
          props.secondary || props.text ? "transparent" : accentColor,
        color:
          props.secondary || props.text
            ? props.sx?.color ?? theme.color.font.color
            : contrastColor,
        transition: "transform 0.12s ease-in-out, box-shadow 0.12s ease-in-out, border-color 0.12s ease-in-out",
        "&:hover": {
          transform: props.text ? "none" : "translate(-1px, -1px)",
          boxShadow: props.text ? "none" : hoverBoxShadow,
          backgroundColor:
            props.secondary || props.text
              ? "transparent"
              : theme.color.primary.accent,
          borderColor:
            props.secondary
              ? isDark
                ? "#f0f0f9"
                : "#191b22"
              : isDark
              ? "#dae2ff"
              : "#191b22",
          color:
            props.secondary || props.text
              ? theme.color.primary.color
              : contrastColor,
          ...props.sx,
        },
        "&:active": {
          transform: props.text ? "none" : "translate(2px, 2px)",
          boxShadow: props.text ? "none" : "1px 1px 0px 0px transparent",
        },
        ...props.sx,
      }}
      onClick={handleClick}
    >
      {props.children}
    </MUIButton>
  );
};
